import React, { useState } from 'react';
import { Slider } from '@wajeez-web/components/ui';

import {
  BaseComponentProps,
  useComponentData,
  useSkeletonHelper,
} from '../helpers/contentHooks';
import { ClickableComponentLayout } from '../layouts/ClickableComponentLayout';
import { SeeMoreModal } from '../modals';
import { Skeleton } from 'antd';
import { MediaHelpers } from '@wajeez-web/core/api';
import { useNavigate } from 'react-router-dom';
import { TextTrimmer } from '../layouts/TextTrimmer';

export interface CircleListParameters {
  title: string | null;
  seeAllTitle: string | null;
  withProgress: 'False' | 'True';
}

export function CircleList({
  component,
  context,
  isLastSibling,
  pageSize,
  pageDataId,
}: BaseComponentProps) {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const { getParameterValue, contents, contentsRequestStatus } =
    useComponentData<CircleListParameters>(
      component,
      context,
      undefined,
      pageSize
    );

  const { isActive } = useSkeletonHelper(
    contentsRequestStatus,
    contents?.length ? true : false
  );

  const seeAllTitle = getParameterValue('seeAllTitle');
  const title = getParameterValue('title');

  return (
    <div className="space-y-[17px]">
      {contentsRequestStatus !== 'loading' &&
      contentsRequestStatus !== 'failed' &&
      contents?.length ? (
        <div className="w-full space-y-[17px] sm:space-y-[7px]">
          <div className="flex justify-between px-4 sm:px-0">
            <div className=" font-bold text-[20px] sm:text-[18px] sm:leading-[30px] sm:text-[#27160E]">
              {title}
            </div>
            {seeAllTitle && (
              <>
                <div
                  className="sm:hidden text-[17px]"
                  onClick={() => setIsModalOpen(true)}
                >
                  {seeAllTitle}
                </div>
                <div
                  className="hidden sm:block h-[33px] -translate-y-[6px] pt-[7px] pb-[8px] px-[18px] bg-[#F2F3F5] rounded-[21px] text-[15px] leading-[18px] font-medium text-black cursor-pointer"
                  onClick={() =>
                    navigate(
                      `/content/see-more?pageDataId=${pageDataId}&componentId=${component?.id}`
                    )
                  }
                >
                  {seeAllTitle}
                </div>
              </>
            )}
          </div>
          <div>
            <div
              className={`w-full sm:pb-[28px] ${
                !isLastSibling && 'border-b-[0.5px]'
              } border-[#C6C6C8] dark:border-dark-divider dark:border-dark-divider`}
            >
              <Slider customStyle="pb-10 px-2 sm:pb-0 sm:px-0 sm:mr-[-30px] desktop:mr-[-20px]">
                {contents?.slice(0, pageSize)?.map((content, i) => (
                  <CircleListItem
                    key={content.id}
                    component={{ ...component?.itemTemplate, uniqueId: i }}
                    context={content}
                  />
                ))}
              </Slider>
            </div>
          </div>
        </div>
      ) : (contentsRequestStatus === 'loading' ||
          contentsRequestStatus === 'failed') &&
        !contents?.length ? (
        <CircleListSkeleton isLastSibling={isLastSibling} isActive={isActive} />
      ) : null}
      {isModalOpen && (
        <SeeMoreModal
          title={title}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          component={component}
          context={context}
          type={component?.type}
        />
      )}
    </div>
  );
}

const CircleListSkeleton = ({
  isLastSibling,
  isActive = true,
}: {
  isLastSibling: boolean | 0 | undefined;
  isActive?: boolean;
}) => {
  return (
    <div className="w-full pr-2 sm:pr-0">
      <div className="pr-2 sm:pr-0">
        <Skeleton.Button
          active={isActive}
          shape="square"
          className="!w-[200px] !h-[30px] dark:bg-dark-medium"
        />
      </div>
      <div
        className={`mt-3 w-full ${
          !isLastSibling && 'border-b-[0.5px]'
        } border-[#C6C6C8] dark:border-dark-divider dark:border-dark-divider sm:border-none`}
      >
        <Slider customStyle="pb-10 px-2 sm:pb-0 sm:px-0 sm:mr-[-30px]">
          {Array.from({ length: 10 }).map((_, i) => {
            return (
              <div
                key={i}
                className="w-[114px] sm:!w-[150px] !flex flex-col justify-center items-center sm:items-end"
              >
                <Skeleton.Node
                  active={isActive}
                  className="!w-full !h-[114px] sm:!h-[150px] !rounded-full dark:bg-dark-medium"
                >
                  <div />
                </Skeleton.Node>
                <Skeleton.Button
                  active={isActive}
                  className="!w-[73px] !h-[16px] mt-2 sm:mt-[31px] dark:bg-dark-medium"
                />
              </div>
            );
          })}
        </Slider>
      </div>
    </div>
  );
};

export interface LargeRectangleGridItemParameters {
  image: string | null;
}

export const CircleListItem: React.FC<BaseComponentProps> = ({
  component,
  context,
}) => {
  const { getParameterValue, content, isLockedContent } =
    useComponentData<LargeRectangleGridItemParameters>(component, context);

  const image = getParameterValue('image');
  return (
    <ClickableComponentLayout content={content}>
      <div className="flex-col w-[114px] sm:w-full sm:px-[30px] desktop:px-[20px] sm:items-start justify-start">
        <div
          className={`relative h-[114px] sm:h-[auto] 2xl:h-[auto] w-full border dark:border-none rounded-full mb-[4px] sm:mb-0 overflow-hidden`}
        >
          <img
            src={MediaHelpers.getPublicMediaById(image)}
            alt="content"
            className="w-full h-full"
          />
          {isLockedContent && (
            <div className="w-[31px] h-[31px] absolute bottom-[10px] left-[10px]">
              <img
                src="../../assets/images/lock.png"
                alt="lock"
                className="block w-full h-full"
              />
            </div>
          )}
        </div>
        <div className="mt-1 sm:mt-[13px] text-[15px] sm:font-bold sm:text-[#545454] text-center ">
          <TextTrimmer textInput={content?.title} linesNumber={2} />
        </div>
        <div className="mt-1 sm:mt-0 text-[12px] sm:text-[15px] sm:font-medium text-[rgba(60, 60, 67, 0.6)] sm:text-[#545454] dark:text-dark-light sm:text-right">
          <TextTrimmer
            textInput={content?.authors?.toString().replaceAll(',', ' و ')}
            linesNumber={2}
          />
        </div>
      </div>
    </ClickableComponentLayout>
  );
};
